/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { message, Spin } from 'antd';
import { useEffect, useState } from 'react';
import { getSystemConfig, getSystemModelProfile } from '~lib/netconnClient';
import constants from '~util/constants';
import handleAuthErr from '~util/handleAuthErr';
import logger from '~util/logger';
import mapDataToConfig from '~util/mapDataToConfig';
import AircardsPane from './AircardsPane';
import ConfigHistoryPane from './ConfigHistoryPane';
import ProvisionModal from './modals/ProvisionModal';
import SystemDetailsPane from './SystemDetailsPane';

const Overview = ({ nai, navigate }) => {
  const [config, setConfig] = useState({});
  const [division, setDivision] = useState();
  const [isLoading, setLoading] = useState(true);
  const [showProvisionModal, setShowProvisionModal] = useState(false);
  const [systemModel, setSystemModel] = useState();

  const configModel = config?.model ?? null;

  const updateConfig = newConfig => setConfig(mapDataToConfig(newConfig));

  useEffect(() => {
    if (!(nai?.match && nai.match(/^[A-Z0-9-]+$/))) {
      message.error(`Invalid NAI received (${nai}), please confirm the NAI and try again...`, 4);
      navigate('/');
    } else {
      setLoading(true);
      getSystemConfig(nai)
        .then(data => {
          if (!!data) {
            if (data.nai !== nai) {
              navigate(`/overview/nai/${data.nai}`);
            } else {
              console.log('setting data');
              setConfig(data);
            }
          } else {
            // New provision scenario
            setShowProvisionModal(true);
            setConfig({});
          }
          setLoading(false);
        })
        .catch(
          handleAuthErr(err => {
            setLoading(false);
            logger.error(err);
            message.error(constants.API_ERR_MESSAGE, 5);
            navigate('/');
          })
        );
    }
  }, [nai, navigate]);

  useEffect(() => {
    if (!!config?.airlineCode) {
      setDivision(config.airlineCode === constants.BA ? constants.BA : constants.CA);
    }
  }, [config]);

  useEffect(() => {
    if (!!configModel) {
      getSystemModelProfile(configModel)
        .then(data => {
          if (!!data) setSystemModel(data);
        })
        .catch(
          handleAuthErr(err => {
            logger.error(err);
            message.error(constants.API_ERR_MESSAGE);
          })
        );
    }
  }, [configModel]);

  const gridStyle = css`
    width: 100%;
    height: 100%;

    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    gap: 15px 15px;
    grid-template-areas:
      'system-details aircards aircards'
      'config-history config-history config-history';
  `;

  const containerStyle = css`
    width: 75%;
    height: 90%;
    max-width: 100rem;

    display: flex;
    justify-content: center;
    align-items: center;

    .overview-spinner {
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;

      .ant-spin-container {
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
  `;

  return (
    <div css={containerStyle}>
      <Spin
        delay={250}
        spinning={isLoading}
        tip="Fetching configuration..."
        wrapperClassName="overview-spinner"
      >
        <div css={gridStyle}>
          <SystemDetailsPane
            config={config}
            division={division}
            gridArea="system-details"
            model={systemModel}
            nai={nai}
            updateConfig={updateConfig}
          />
          <AircardsPane
            aircards={config?.aircards ?? null}
            config={config}
            gridArea="aircards"
            updateConfig={updateConfig}
          />
          <ConfigHistoryPane
            currentEventID={config?.eventID ?? null}
            division={division}
            gridArea="config-history"
            nai={!!config ? nai : null}
          />
        </div>
      </Spin>
      <ProvisionModal
        cancel={() => navigate('/')}
        close={() => setShowProvisionModal(false)}
        nai={nai}
        updateConfig={updateConfig}
        visible={showProvisionModal}
      />
    </div>
  );
};

export default Overview;
